import { ClientCartDetail, ICartItem } from "common/types/carts";
import { Optional } from "common/utils/types";
import { pluralized, toHumanizedList } from "common/utils/strings";
import { UnavailableReason } from "common/types/fulfillments";

import { CartRefillTooSoonSummary } from "client/carts/types";

import { SubscriptionOptionType, OptionType, CheckoutStep } from "../types";

export const getSubscriptionCartItems = (cartItems: ICartItem[]) => {
  return cartItems.filter((cartItem) => !!cartItem.subscription);
};

export const optionTypeComplete = (
  options: OptionType | SubscriptionOptionType,
  cartItems: ICartItem[],
) => !cartItems.some((cartItem) => !options[cartItem.id]);

export const subscriptionOptionTypeComplete = (
  options: SubscriptionOptionType,
  cartItems: ICartItem[],
) => optionTypeComplete(options, getSubscriptionCartItems(cartItems));

export const steps = [CheckoutStep.Items, CheckoutStep.Delivery, CheckoutStep.Payment];

/**
 * The cart-item fields step validation reads. Narrower than `ICartItem` so the rules can
 * be exercised without standing up a whole cart.
 */
export type ValidatedCartItem = Pick<
  ICartItem,
  | "missing_required_patient_fields_message"
  | "is_available"
  | "flavor_selection"
  | "unavailable_reason"
  | "is_pickup"
> & {
  patient: Optional<{ id: number }>;
  item: Pick<ICartItem["item"], "flavor_options">;
};

/** The cart fields step validation reads. */
export type ValidatedCart = Pick<
  ClientCartDetail,
  | "is_closed"
  | "is_delivery_info_complete"
  | "missing_required_client_fields_message"
  | "payment_method"
> & { items: ValidatedCartItem[] };

/**
 * Whether the only thing wrong with this item is where the order is going -- no pharmacy
 * that can ship to the destination's state has it in stock.
 *
 * The client fixes it by choosing another address, so it must not send them back to the
 * Items step, which holds no address affordance, and it must not let them reach Payment,
 * whose checkout call would reject the item outright.
 *
 * The reason is computed from shipping supply alone, so a pickup item never carries it.
 */
export const isUnfillableAtDestination = (cartItem: ValidatedCartItem): boolean =>
  !cartItem.is_pickup &&
  cartItem.unavailable_reason === UnavailableReason.ControlledStateUnfillable;

export const validators: {
  [K in CheckoutStep]: (
    cart: ValidatedCart,
    refillTooSoonSummary: CartRefillTooSoonSummary,
  ) => boolean;
} = {
  items: (cart, refillTooSoonSummary) =>
    !cart.missing_required_client_fields_message &&
    cart.items.every(
      (cartItem) =>
        !!cartItem.patient &&
        !cartItem.missing_required_patient_fields_message &&
        (cartItem.is_available || isUnfillableAtDestination(cartItem)) &&
        // either have a selection, or item doesn't need selections
        (!cartItem.item.flavor_options.length || !!cartItem.flavor_selection),
    ) &&
    refillTooSoonSummary.cart_items.every(
      ({ is_valid_by_refill_too_soon: isValidByRTS }) => isValidByRTS,
    ),
  delivery: (cart) => cart.is_delivery_info_complete && !cart.items.some(isUnfillableAtDestination),
  payment: (cart) => !!cart.payment_method,
};

export const inferStep = (cart: ValidatedCart, refillTooSoonSummary: CartRefillTooSoonSummary) => {
  if (cart.is_closed) {
    return null;
  }

  if (!validators.items(cart, refillTooSoonSummary)) {
    return CheckoutStep.Items;
  }

  if (!validators.delivery(cart, refillTooSoonSummary)) {
    return CheckoutStep.Delivery;
  }

  return CheckoutStep.Payment;
};

type HeldCardSentenceArgs = {
  isDestinationBasisEnabled: boolean;
  hasControlledHold: boolean;
  hasApprovalHold: boolean;
  /** Product lines of the items carrying the controlled hold. */
  names: string[];
  /** The state the controlled rules are judged against. */
  stateName: Optional<string>;
};

/**
 * Sentence explaining why the card is not charged yet, or `null` when nothing holds it.
 *
 * Off the destination basis the controlled rules answer to a state the client cannot change
 * from checkout, so that path names no state.
 */
export const getHeldCardSentence = ({
  isDestinationBasisEnabled,
  hasControlledHold,
  hasApprovalHold,
  names,
  stateName,
}: HeldCardSentenceArgs): Optional<string> => {
  if (!isDestinationBasisEnabled) {
    return hasControlledHold || hasApprovalHold
      ? "Since your cart contains a prescription item, your veterinarian will be notified and will review your request. Your order will not be finalized, and your card will not be charged, until your veterinarian has approved your request."
      : null;
  }

  // A pickup order has the hold but no shipping address, so the state qualifies the clause
  // rather than gating it.
  const controlledClause =
    hasControlledHold && names.length
      ? `your ${toHumanizedList(names)} ${pluralized(names.length, "prescription")}${
          stateName ? ` for ${stateName}` : ""
        }`
      : null;

  if (controlledClause && hasApprovalHold) {
    return `Your card won't be charged until your veterinarian reviews this order, including ${controlledClause}.`;
  }
  if (controlledClause) {
    return `Your card won't be charged until your veterinarian confirms ${controlledClause}.`;
  }
  if (hasApprovalHold) {
    return "Your card won't be charged until your veterinarian reviews and approves this order.";
  }
  return null;
};

export const isValidStep = (
  inferredStep: CheckoutStep,
  step: Optional<string>,
): step is CheckoutStep => {
  const inferredStepIndex = steps.indexOf(inferredStep);
  // cast is `CartStep` because `indexOf` should be safe for any type, but resolve to -1 if not valid
  const stepIndex = steps.indexOf(step as CheckoutStep);
  return inferredStepIndex !== -1 && stepIndex !== -1 && stepIndex <= inferredStepIndex;
};
